'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import MapOverlay from '@alltrails/amplitude/enums/MapOverlay';
import logOSMCTAClicked from '@alltrails/amplitude/events/logOSMCTAClicked';
import logPOIDrawerDismissed from '@alltrails/amplitude/events/logPOIDrawerDismissed';
import logPOISelected from '@alltrails/amplitude/events/logPOISelected';
import {
  useClientNavigationPathname,
  useClientNavigationPushState,
  useClientNavigationSearchParams
} from '@alltrails/core/hooks/useClientNavigation';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useValueRef from '@alltrails/core/hooks/useValueRef';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import useMap from '../../hooks/useMap';
import { useAppDispatch, useAppSelector } from '../../redux';
import { setPendingFlyTo, updateIsMapFeatureSelected } from '../../redux/reducer';
import { getMapBaseLayer, getMapOverlay } from '../../utils/amplitudeHelpers';
import useRouteToPoint from '../CustomRoute/DrawControls/useRouteToPoint';
import ClickableIconsPopup from './ClickableIconsPopup';
import usePoiInteractions from './usePoiInteractions';

// On our new base map style, we have added additional JSON that makes some of our OSM POIs clickable and interactive
// The component renders a popup when an OSM POI is clicked and provides a way to add the POI to a custom route

const ClickableIcons = () => {
  const map = useMap();
  const dispatch = useAppDispatch();
  const { isEditing, baseStyleId, dynamicStyleIds, staticStyleIds } = useAppSelector(state => ({
    isEditing: state.customRoute.isEditing,
    baseStyleId: state.map.baseStyleId,
    dynamicStyleIds: state.map.dynamicStyleIds,
    staticStyleIds: state.map.staticStyleIds
  }));
  const { popupFeature, setPopupFeature } = usePoiInteractions();
  const isSmartRouting = useAppSelector(state => state.customRoute.isSmartRouting);
  const isSmartRoutingRef = useValueRef(isSmartRouting);
  const { routeToPoint } = useRouteToPoint(isSmartRoutingRef);
  const languageRegionCode = useLanguageRegionCode();
  const pushState = useClientNavigationPushState();
  const searchParams = useClientNavigationSearchParams();
  const didUseCTARef = useRef(false);
  const prevPopupFeatureRef = useRef(popupFeature);
  const loggedPopupFeatureRef = useRef(popupFeature);
  const pathname = useClientNavigationPathname();

  const returnTo = useMemo(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    return window.location.pathname + window.location.search;
  }, []);

  const buildCustomRouteUrl = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    if (popupFeature?.geometry) {
      params.set('lat', popupFeature?.geometry?.[1].toString());
      params.set('lng', popupFeature?.geometry?.[0].toString());
    }
    if (returnTo) {
      params.set('returnTo', returnTo);
    }
    return wrapUrlSafe(`/explore/custom-routes/new?${params.toString()}`, languageRegionCode);
  }, [searchParams, returnTo, languageRegionCode, popupFeature?.geometry]);

  useEffect(() => {
    if (popupFeature?.geometry && popupFeature !== loggedPopupFeatureRef.current) {
      loggedPopupFeatureRef.current = popupFeature;
      const poiDetailedLocation = isEditing
        ? 'custom route builder'
        : pathname.includes('/poi/')
          ? 'explore map poi focused state'
          : 'explore map default state';
      logPOISelected({
        lat: map?.getCenter().lat,
        lng: map?.getCenter().lng,
        map_base_layer: getMapBaseLayer(baseStyleId),
        map_overlay: [...dynamicStyleIds, ...staticStyleIds].map(id => getMapOverlay(id) as MapOverlay),
        osm_category: String(popupFeature.icon) || undefined,
        osm_cta: isEditing ? 'add to route' : 'build custom route',
        osm_lat: popupFeature.geometry[1],
        osm_lng: popupFeature.geometry[0],
        osm_name: popupFeature.name ? String(popupFeature.name) : undefined,
        poi_location: isEditing ? 'custom route builder' : 'explore map',
        poi_source: 'osm',
        poi_detailed_location: poiDetailedLocation
      });
    } else if (!popupFeature) {
      loggedPopupFeatureRef.current = undefined;
    }
  }, [popupFeature, isEditing, map, baseStyleId, dynamicStyleIds, staticStyleIds, pathname]);

  useEffect(() => {
    const prev = prevPopupFeatureRef.current;
    prevPopupFeatureRef.current = popupFeature;

    if (prev && !popupFeature && !didUseCTARef.current) {
      logPOIDrawerDismissed({
        osm_category: String(prev.icon) || undefined,
        poi_location: isEditing ? 'custom route builder' : 'explore map',
        poi_source: 'osm'
      });
    }
    didUseCTARef.current = false;
  }, [popupFeature, isEditing]);

  const handleAddToRoute = useCallback(() => {
    if (!popupFeature?.geometry) {
      return;
    }
    didUseCTARef.current = true;

    logOSMCTAClicked({
      osm_category: String(popupFeature.icon) || undefined,
      osm_cta: isEditing ? 'add to route' : 'build custom route',
      osm_lat: popupFeature.geometry[1],
      osm_lng: popupFeature.geometry[0],
      osm_name: popupFeature.name ? String(popupFeature.name) : undefined,
      poi_location: isEditing ? 'custom route builder' : 'explore map'
    });
    if (isEditing) {
      routeToPoint({ coordinates: { lat: popupFeature.geometry[1], lng: popupFeature.geometry[0] }, isOSMPOIAdd: true });
    } else {
      dispatch(setPendingFlyTo({ center: [popupFeature.geometry[0], popupFeature.geometry[1]], zoom: 17 }));
      pushState(buildCustomRouteUrl());
    }
    dispatch(updateIsMapFeatureSelected(false));
    setPopupFeature(undefined);
  }, [popupFeature, routeToPoint, isEditing, pushState, dispatch, setPopupFeature, buildCustomRouteUrl]);

  if (baseStyleId !== 'alltrailsOutdoorsV3') {
    return null;
  }

  return popupFeature ? <ClickableIconsPopup popupFeature={popupFeature} isEditing={!!isEditing} onAddToRoute={handleAddToRoute} /> : null;
};

export default ClickableIcons;
